import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetWorkingWeekResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import type { MessageKey } from '@whinerp/contracts/i18n';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * The working week (F-025, F-087).
 *
 * It was a constant - Sunday to Thursday, which is the Gulf - so a European
 * workspace's SLA clocks ran through Sunday and stopped on Friday, and every
 * deadline was wrong by two days in the direction that flatters.
 *
 * The panel says what the setting DOES, because a list of ticked days with no
 * explanation reads as a calendar preference rather than as the thing that
 * decides when a ticket breaches.
 *
 * The times are in the workspace time zone and the panel names it, because
 * "opens at 09:00" means nothing without saying whose nine o'clock.
 */
export function WorkingWeekPanel(): JSX.Element {
  const queryClient = useQueryClient();

  const week = useQuery<GetWorkingWeekResult, ApiError>({
    queryKey: ['working-week'],
    queryFn: () => api.getWorkingWeek({}),
  });

  if (week.isPending) return <Skeleton className="h-block-md w-full" />;
  if (week.isError) return <></>;

  /*
   * Keyed on what came back, so the form rebuilds when the saved week changes
   * rather than keeping the ticks somebody had before a refetch.
   */
  return (
    <WorkingWeekForm
      key={`${week.data.days.join('')}-${String(week.data.startMinute)}`}
      initial={week.data}
      onSaved={() => void queryClient.invalidateQueries({ queryKey: ['working-week'] })}
    />
  );
}

function WorkingWeekForm({
  initial,
  onSaved,
}: {
  initial: GetWorkingWeekResult;
  onSaved: () => void;
}): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const [days, setDays] = useState<number[]>([...initial.days]);
  const [startMinute, setStartMinute] = useState(initial.startMinute);
  const [endMinute, setEndMinute] = useState(initial.endMinute);

  const save = useMutation({
    mutationFn: () => api.setWorkingWeek({ body: { days, startMinute, endMinute } }),
    onSuccess: onSaved,
  });

  /*
   * The last day cannot be unticked.
   *
   * A week with no working days is a clock that can never advance, and the
   * server refuses it - so the control refuses first, rather than letting
   * somebody make the choice and then be told no.
   */
  const toggle = (day: number): void => {
    const next = days.includes(day) ? days.filter((entry) => entry !== day) : [...days, day];
    if (next.length === 0) return;
    setDays(next.sort((a, b) => a - b));
  };

  const asTime = (minutes: number): string =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  const fromTime = (value: string): number => {
    const [hours, mins] = value.split(':');
    return Number(hours ?? 0) * 60 + Number(mins ?? 0);
  };

  return (
    <Panel>
      <div data-component="WorkingWeekPanel" className="flex flex-col gap-3">
        <SectionLabel>{t('week.title')}</SectionLabel>
        {/* What it decides, not what it is. */}
        <p className="text-meta text-ink-meta">{t('week.body')}</p>

        <fieldset className="flex flex-col gap-1">
          <legend className="text-micro uppercase tracking-label text-ink-label">
            {t('week.days')}
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <label key={day} className="flex items-center gap-1.5 text-dense text-ink-600">
                <input
                  type="checkbox"
                  data-working-day={day}
                  checked={days.includes(day)}
                  onChange={() => toggle(day)}
                  className="h-icon-inline w-icon-inline rounded-control border-control"
                />
                {t(`week.day.${day}` as MessageKey)}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-dense text-ink">
            {t('week.start')}
            <input
              type="time"
              data-working-start
              value={asTime(startMinute)}
              onChange={(event) => {
                const next = fromTime(event.currentTarget.value);
                setStartMinute(next);
              }}
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink">
            {t('week.end')}
            <input
              type="time"
              data-working-end
              value={asTime(endMinute)}
              onChange={(event) => {
                const next = fromTime(event.currentTarget.value);
                setEndMinute(next);
              }}
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
            />
          </label>
        </div>
        {/* Whose nine o'clock. */}
        <p className="text-meta text-ink-meta">
          {t('week.zone', { values: { zone: initial.timeZone } })}
        </p>

        {save.isError ? (
          <p data-working-week-refusal className="text-dense text-danger-text">
            {errorMessage(save.error)}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            intent="primary"
            size="small"
            data-working-week-save
            busy={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('week.save')}
          </Button>
          {save.isSuccess ? (
            <span data-working-week-saved className="text-meta text-ink-meta">
              {t('week.saved')}
            </span>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
